// Platform operations contracts (module 19, ADR-012): background jobs, deployment profile, recovery
// and controls. Job payloads are never exposed — only their keys — and error text is redacted.
import { z } from 'zod';
import type { IntegrationHealthSummary } from './opsview.ts';

export const JOB_STATES = ['queued', 'scheduled', 'retrying', 'in_flight', 'dead_letter', 'resolved', 'dispatched'] as const;
export type JobStateName = (typeof JOB_STATES)[number];

export const OperationsJobsQuery = z.object({
  state: z.enum(JOB_STATES).optional(),
  siteId: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  after: z.string().optional(),
});
export type OperationsJobsQuery = z.infer<typeof OperationsJobsQuery>;

const reason = z.string().trim().min(3, 'v.opsReasonShort').max(500, 'v.tooLong');

export const JobRetryRequest = z.object({
  reason,
  expectedAttempts: z.number().int().min(0),
  /** The operator checked that the external effect did not happen (or may safely repeat). */
  externalChecked: z.boolean().default(false),
});
export type JobRetryRequest = z.infer<typeof JobRetryRequest>;

export const JobCommandRequest = z.object({ reason, expectedAttempts: z.number().int().min(0) });
export type JobCommandRequest = z.infer<typeof JobCommandRequest>;

export interface OperationsJob {
  readonly id: string;
  readonly topic: string;
  readonly effect: 'external' | 'internal' | 'none';
  readonly aggregateType: string;
  readonly siteId: string | null;
  readonly state: JobStateName;
  readonly attempts: number;
  readonly operatorRetries: number;
  readonly createdAt: string;
  readonly availableAt: string;
  readonly leasedUntil: string | null;
  readonly dispatchedAt: string | null;
  readonly deadLetteredAt: string | null;
  readonly resolvedAt: string | null;
  /** Redacted, truncated diagnostic text. */
  readonly lastError: string | null;
  readonly correlationId: string | null;
  /** Payload field names only; values are never shown. */
  readonly payloadKeys: readonly string[];
  /** Commands this caller may run on this job right now. */
  readonly allowed: readonly ('retry' | 'dead_letter' | 'resolve')[];
  /** ADR-029: safe class of the last error (timeout, unavailable, …), derived from the redacted text. */
  readonly errorClass?: 'timeout' | 'unavailable' | 'rejected' | 'invalid' | 'conflict' | 'restore_fence' | 'outcome_unknown' | 'other' | null;
}

export interface OperationsJobAction {
  readonly id: string;
  readonly action: 'retry' | 'dead_letter' | 'resolve';
  readonly reason: string;
  readonly actorUserId: string;
  readonly previousState: string;
  readonly previousAttempts: number;
  readonly externalChecked: boolean;
  readonly createdAt: string;
}

export interface OperationsJobDetail {
  readonly job: OperationsJob;
  readonly history: readonly OperationsJobAction[];
  readonly resolution: string | null;
  /**
   * ADR-029: the tracked provider outcome of an external operation this job carries (credential
   * operation or barrier command). While it is unknown a replay is refused until read-back settles it.
   */
  readonly externalOutcome?: { readonly kind: 'credential_operation' | 'gate_command'; readonly state: string; readonly replayBlocked: boolean } | null;
}

export interface OperationsJobsResponse {
  readonly items: readonly OperationsJob[];
  readonly nextCursor: string | null;
}

/** PD-650 (clamav stream): read-only malware scanner status for the operations health panel. */
export interface MalwareScannerHealth {
  readonly type: 'none' | 'clamav';
  readonly label: string;
  readonly status: 'not_configured' | 'ok' | 'unavailable';
  readonly engine: string | null;
  readonly signatureVersion: number | null;
  readonly signatureDate: string | null;
  readonly signatureAgeDays: number | null;
  readonly maxSignatureAgeDays: number;
  /** The signature database is older than `maxSignatureAgeDays` (updates are not arriving). */
  readonly signaturesStale: boolean;
}

export interface OperationsSummaryResponse {
  readonly generatedAt: string;
  /** Counts in the caller's scope (their sites; organisation-level only with a tenant-wide grant). */
  readonly counts: Readonly<Record<JobStateName, number>>;
  readonly oldestDueAgeSeconds: number | null;
  readonly workers: { readonly active: number; readonly lastSeenAt: string | null };
  readonly health: {
    readonly database: 'ok' | 'unavailable';
    readonly schema: 'current' | 'pending' | 'ahead' | 'drift' | 'unknown';
    readonly objectStore: 'ok' | 'unavailable';
    readonly objectStoreKind: string;
    /** PD-650: malware scanner configuration and engine status (never a host name). */
    readonly malwareScanner?: MalwareScannerHealth;
  };
  readonly scope: { readonly allSites: boolean; readonly siteIds: readonly string[] };
  /** PB-19-08 (PD-555): device, connector, edge-agent and event-poll health in the same scope (counts only). */
  readonly integrations: IntegrationHealthSummary;
}

export interface OperationsProfileResponse {
  readonly activeProfile: string;
  readonly build: { readonly version: string | null; readonly schemaVersion: string | null };
  readonly profiles: ReadonlyArray<{
    readonly id: string;
    readonly features: readonly string[];
    readonly status: 'rehearsed-locally' | 'documented' | 'not-started';
    readonly region: string | null;
    readonly residencyClaimable: boolean;
    readonly decisionsRequired: readonly string[];
    readonly availability: string;
    readonly dataFlows: ReadonlyArray<{ readonly id: string; readonly location: string | null; readonly status: string }>;
  }>;
  readonly service: { readonly edition: string | null; readonly agreement: string | null; readonly contacts: readonly string[]; readonly escalation: string | null; readonly responseTerms: string | null };
  readonly recovery: { readonly rpo: string | null; readonly rto: string | null; readonly backupRetention: string | null; readonly failover: string };
  readonly runs: ReadonlyArray<{
    readonly id: string; readonly kind: string; readonly finishedAt: string; readonly outcome: 'succeeded' | 'failed'; readonly durationMs: number; readonly schemaVersion: string | null;
    /** PB-19-06 (PD-562): backup with a succeeded restore rehearsal of the same manifest / rehearsal succeeded; null for other kinds. */
    readonly verified: boolean | null;
  }>;
  readonly controls: ReadonlyArray<{ readonly id: string; readonly requirementRef: string | null; readonly status: 'evidenced-locally' | 'gap'; readonly owner: string | null; readonly reviewedAt: string | null; readonly reviewer: string | null; readonly evidence: readonly string[] }>;
}
